import { QueryClient } from '@tanstack/react-query';

import { ApiError } from '@/lib/errors';

/**
 * Default stale time for queries (5 minutes).
 */
const DEFAULT_STALE_TIME = 5 * 60 * 1000;

/**
 * Maximum retry attempts for failed queries.
 */
const MAX_RETRY_COUNT = 3;

/**
 * Custom retry function that doesn't retry on client errors (4xx).
 */
function shouldRetry(failureCount: number, error: Error): boolean {
    // Don't retry on client errors (4xx)
    if (error instanceof ApiError && error.isClientError()) {
        return false;
    }

    // Retry up to MAX_RETRY_COUNT times for other errors
    return failureCount < MAX_RETRY_COUNT;
}

/**
 * Configured QueryClient instance for the application.
 *
 * Features:
 * - 5 minute default stale time
 * - Smart retry logic (no retry on 4xx errors)
 * - Garbage collection after 10 minutes
 */
export const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            staleTime: DEFAULT_STALE_TIME,
            gcTime: 10 * 60 * 1000, // 10 minutes
            retry: shouldRetry,
            refetchOnWindowFocus: false,
        },
        mutations: {
            retry: false,
        },
    },
});
